import { Box, Stack } from '@mui/material';
import React from 'react';
import {
  ALL_PUSH_NOTIFICATION_FIELDS,
  PUSH_NOTIFICATION_CATEGORIES,
  isAnyPushTypeEnabled,
} from '../../../constants/pushNotificationCategories';
import { GlobalConfiguration } from '../../../models/GlobalConfiguration';
import { StyledButton } from '../../styles/StyledButtons';
import StyledFieldDescription from '../../styles/StyledFieldDescription';
import { StyledInput, StyledSwitch } from '../../styles/StyledInputs';
import StyledSection from '../../styles/StyledSection';
import { StyledFieldRow, StyledHeading, StyledLabel, StyledTypography } from '../../styles/styles';
import ConfigFieldLabel from './ConfigFieldLabel';
import { GLOBAL_CONFIG_FIELD_LABELS } from './globalConfigFieldConfig';

type NotificationsSectionProps = {
  config: GlobalConfiguration;
  dirtyFields: Set<string>;
  onChange: (field: string, value: unknown) => void;
  onSetAllPushTypes: (enabled: boolean) => void;
};

export default function NotificationsSection({
  config,
  dirtyFields,
  onChange,
  onSetAllPushTypes,
}: NotificationsSectionProps) {
  const enabledPushTypeCount = ALL_PUSH_NOTIFICATION_FIELDS.filter(
    (field) => !!config[field],
  ).length;

  return (
    <StyledSection tone="tone1">
      <StyledHeading tone="tone1" headerVariant="heading2">
        Notification Configuration
      </StyledHeading>
      <StyledHeading tone="tone1" headerVariant="subheading" sx={{ mt: 0, mb: 1 }}>
        Mobile push notifications
      </StyledHeading>
      <StyledFieldDescription stacked sx={{ mb: 2 }}>
        Choose which events send a push to the Fides mobile app. Each type can be enabled or
        disabled independently. Investors who have turned off notifications in the app will not
        receive pushes even when a type is enabled here.
      </StyledFieldDescription>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <StyledTypography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
          {enabledPushTypeCount} of {ALL_PUSH_NOTIFICATION_FIELDS.length} types enabled
          {isAnyPushTypeEnabled(config) ? '' : ' (no pushes will be sent)'}
        </StyledTypography>
        <StyledButton variant="outlined" size="small" onClick={() => onSetAllPushTypes(true)}>
          Enable all
        </StyledButton>
        <StyledButton variant="outlined" size="small" onClick={() => onSetAllPushTypes(false)}>
          Disable all
        </StyledButton>
      </Stack>
      {PUSH_NOTIFICATION_CATEGORIES.map((category) => (
        <Box key={category.id} sx={{ mb: 3 }}>
          <StyledHeading tone="tone1" headerVariant="subheading" sx={{ mb: 0.5 }}>
            {category.title}
          </StyledHeading>
          <StyledFieldDescription stacked sx={{ mb: 1.5 }}>
            {category.description}
          </StyledFieldDescription>
          {category.types.map((type) => (
            <StyledFieldRow
              unrestrictedWidth
              sx={{ mb: 2 }}
              key={type.field}
              data-field={type.field}
            >
              <StyledSwitch
                checked={!!config[type.field]}
                onChange={(e) => onChange(type.field, e.target.checked)}
              />
              <Box sx={{ ml: 2 }}>
                <StyledLabel unrestricted>
                  <ConfigFieldLabel
                    fieldKey={type.field}
                    label={type.label}
                    dirtyFields={dirtyFields}
                  />
                </StyledLabel>
                <StyledFieldDescription stacked>
                  {type.description}
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    Type key: {type.notificationType}
                  </Box>
                </StyledFieldDescription>
              </Box>
            </StyledFieldRow>
          ))}
        </Box>
      ))}
      <StyledHeading tone="tone1" headerVariant="subheading" sx={{ mt: 1, mb: 1 }}>
        Promotion timing
      </StyledHeading>
      <StyledFieldRow stacked sx={{ mb: 2 }}>
        <StyledLabel stacked>
          <ConfigFieldLabel
            fieldKey="pushNotificationDelayMinutes"
            label={GLOBAL_CONFIG_FIELD_LABELS.pushNotificationDelayMinutes}
            dirtyFields={dirtyFields}
          />
        </StyledLabel>
        <StyledFieldDescription stacked>
          Minimum delay in minutes before delayed promotion pushes are sent. Set to 0 to allow
          immediate delivery when &quot;Delayed promotion&quot; is enabled.
        </StyledFieldDescription>
        <StyledTypography
          component="p"
          fontSize="0.75rem"
          sx={{ display: 'block', mt: -0.5, mb: 1, color: 'text.secondary' }}
        >
          Actual delivery can take longer depending on device state and network conditions.
        </StyledTypography>
        <StyledInput
          type="number"
          value={config.pushNotificationDelayMinutes || 0}
          onChange={(e) => onChange('pushNotificationDelayMinutes', parseInt(e.target.value) || 0)}
          inputProps={{ min: 0, step: 1 }}
          data-field="pushNotificationDelayMinutes"
        />
      </StyledFieldRow>
    </StyledSection>
  );
}
